import { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "./components/ProtectedRoute";
import PlatformRoute from "./components/PlatformRoute";
import PartnerRoute from "./components/PartnerRoute";
import logger from "./lib/logger";
// Envuelve React.lazy para recuperarse de chunks obsoletos tras un deploy
// (evita el `SyntaxError: Unexpected token '<'` cuando S3/CloudFront sirve
// index.html en lugar de un chunk borrado). Ver lib/lazyWithRetry.js.
import lazy from "./lib/lazyWithRetry";

// =====================================================================
// Route components
// =====================================================================
// Public / unauthenticated routes stay eager:
//   - They are the first thing every visitor downloads.
//   - They are small, and a lazy boundary here adds latency without
//     saving payload (the user IS going to need them).
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SSOCallback from "./pages/SSOCallback";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Unsubscribe from "./pages/Unsubscribe";

// Everything authenticated is lazy. Each route becomes its own chunk;
// the initial bundle stops carrying ag-grid, recharts, and the heavy
// page components until the user actually navigates there.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Flow = lazy(() => import("./pages/Flow"));
const FlowV2 = lazy(() => import("./pages/FlowV2"));
const Projections = lazy(() => import("./pages/Projections"));
const ProjectionAccuracy = lazy(() => import("./pages/ProjectionAccuracy"));
const WhatIf = lazy(() => import("./pages/WhatIf"));
const CompareVersions = lazy(() => import("./pages/CompareVersions"));
const TransactionsV2 = lazy(() => import("./pages/TransactionsV2"));
const ImportPage = lazy(() => import("./pages/ImportPage"));
const FlowCategories = lazy(() => import("./pages/FlowCategories"));
const Companies = lazy(() => import("./pages/Companies"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Versions = lazy(() => import("./pages/Versions"));
const Users = lazy(() => import("./pages/Users"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Audit = lazy(() => import("./pages/Audit"));
const Settings = lazy(() => import("./pages/Settings"));
const SubscriptionSettings = lazy(() => import("./pages/SubscriptionSettings"));
const FxRates = lazy(() => import("./pages/FxRates"));
const Help = lazy(() => import("./pages/Help"));
const Suggestions = lazy(() => import("./pages/Suggestions"));
const Rules = lazy(() => import("./pages/Rules"));
const ImportTemplates = lazy(() => import("./pages/ImportTemplates"));
const Connections = lazy(() => import("./pages/Connections"));
const Learn = lazy(() => import("./pages/Learn"));
const Roles = lazy(() => import("./pages/Roles"));
const AccessRequests = lazy(() => import("./pages/AccessRequests"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));

// Platform admin routes — lazy too (only a tiny fraction of users ever hits these).
const PlatformDashboard = lazy(() => import("./pages/platform/PlatformDashboard"));
const TenantList = lazy(() => import("./pages/platform/TenantList"));
const TenantDetail = lazy(() => import("./pages/platform/TenantDetail"));
const PlatformAlerts = lazy(() => import("./pages/platform/Alerts"));
const EmailTemplates = lazy(() => import("./pages/platform/EmailTemplates"));
const RevenueIntelligence = lazy(() => import("./pages/platform/RevenueIntelligence"));
const EarlyMetrics = lazy(() => import("./pages/platform/EarlyMetrics"));
const PlanManager = lazy(() => import("./pages/platform/PlanManager"));
const DocumentosTributarios = lazy(() => import("./pages/platform/DocumentosTributarios"));
const PartnersAdmin = lazy(() => import("./pages/platform/Partners"));
const PartnerPortal = lazy(() => import("./pages/PartnerPortal"));

/**
 * Fallback shown while a lazy route chunk is being fetched. Centered spinner
 * with no chrome so it doesn't visually conflict with whatever layout the
 * destination page ends up rendering.
 */
function RouteSuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" aria-label="Cargando" />
    </div>
  );
}

export default function AppRoutes() {
  logger.debug("[AppRoutes] Rendering, current path:", window.location.pathname);
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/sso-callback" element={<SSOCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Invitación: misma pantalla, textos de "crea tu contraseña". El correo
            de invitación apunta acá; los dos caminos consumen el mismo token. */}
        <Route path="/set-password" element={<ResetPassword modo="invitacion" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* Baja de la secuencia de correos: pública, se llega desde el enlace del
            correo con sid+token. Antes esta URL era una página en blanco (React
            Router no la matcheaba) y el pie usaba mailto por eso — ítem 10. */}
        <Route path="/unsubscribe" element={<Unsubscribe />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/flow" element={<ProtectedRoute requiredPermission={["flow", "view"]}><Flow /></ProtectedRoute>} />
        <Route path="/flow-v2" element={<ProtectedRoute requiredPermission={["projections", "view"]}><FlowV2 /></ProtectedRoute>} />
        <Route path="/projections" element={<ProtectedRoute requiredPermission={["projections", "view"]}><Projections /></ProtectedRoute>} />
        <Route path="/accuracy" element={<ProtectedRoute requiredPermission={["projections", "view"]}><ProjectionAccuracy /></ProtectedRoute>} />
        <Route path="/whatif" element={<ProtectedRoute requiredPermission={["projections", "view"]}><WhatIf /></ProtectedRoute>} />
        <Route path="/compare-versions" element={<ProtectedRoute requiredPermission={["projections", "view"]}><CompareVersions /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute requiredPermission={["transactions", "view"]}><TransactionsV2 /></ProtectedRoute>} />
        <Route path="/import" element={<ProtectedRoute requiredPermission={["transactions", "import"]}><ImportPage /></ProtectedRoute>} />
        <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        <Route path="/learn" element={<ProtectedRoute><Learn /></ProtectedRoute>} />
        <Route path="/suggestions" element={<ProtectedRoute><Suggestions /></ProtectedRoute>} />
        <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/payment-cancelled" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
        <Route path="/rules" element={<ProtectedRoute requiredPermission={["transactions", "edit"]}><Rules /></ProtectedRoute>} />

        {/* Admin Routes */}
        <Route path="/companies" element={<ProtectedRoute requiredPermission={["companies", "view"]}><Companies /></ProtectedRoute>} />
        <Route path="/accounts" element={<ProtectedRoute requiredPermission={["accounts", "view"]}><Accounts /></ProtectedRoute>} />
        <Route path="/versions" element={<ProtectedRoute requiredPermission={["versions", "view"]}><Versions /></ProtectedRoute>} />
        <Route path="/flow-categories" element={<ProtectedRoute requiredPermission={["flow_items", "view"]}><FlowCategories /></ProtectedRoute>} />
        <Route path="/fx-rates" element={<ProtectedRoute requiredPermission={["fx_rates", "view"]}><FxRates /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute requiredPermission={["users", "view"]}><Users /></ProtectedRoute>} />
        <Route path="/access-requests" element={<ProtectedRoute requiredPermission={["users", "view"]}><AccessRequests /></ProtectedRoute>} />
        <Route path="/roles" element={<ProtectedRoute requiredPermission={["users", "view"]}><Roles /></ProtectedRoute>} />
        <Route path="/audit" element={<ProtectedRoute requiredPermission={["audit", "view"]}><Audit /></ProtectedRoute>} />
        <Route path="/import-templates" element={<ProtectedRoute requiredPermission={["transactions", "import"]}><ImportTemplates /></ProtectedRoute>} />
        {/* Conexiones a ERPs. Gatea con accounts.view igual que su backend: la
            pantalla lista fuentes que alimentan las cuentas. */}
        <Route path="/connections" element={<ProtectedRoute requiredPermission={["accounts", "view"]}><Connections /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute requiredPermission={["settings", "view"]}><Settings /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute requiredPermission={["settings", "view"]}><SubscriptionSettings /></ProtectedRoute>} />

        {/* Platform Admin Routes */}
        <Route path="/platform/dashboard" element={<PlatformRoute><PlatformDashboard /></PlatformRoute>} />
        <Route path="/platform/plans" element={<PlatformRoute><PlanManager /></PlatformRoute>} />
        <Route path="/platform/tenants" element={<PlatformRoute><TenantList /></PlatformRoute>} />
        <Route path="/platform/tenants/:tenant_id" element={<PlatformRoute><TenantDetail /></PlatformRoute>} />
        <Route path="/platform/alerts" element={<PlatformRoute><PlatformAlerts /></PlatformRoute>} />
        <Route path="/platform/email-templates" element={<PlatformRoute><EmailTemplates /></PlatformRoute>} />
        <Route path="/platform/revenue" element={<PlatformRoute><RevenueIntelligence /></PlatformRoute>} />
        <Route path="/platform/metrics" element={<PlatformRoute><EarlyMetrics /></PlatformRoute>} />
        <Route path="/platform/documents" element={<PlatformRoute><DocumentosTributarios /></PlatformRoute>} />
        <Route path="/platform/partners" element={<PlatformRoute><PartnersAdmin /></PlatformRoute>} />

        {/* Portal del partner: dentro de la app del tenant (una consultora que
            refiere TAMBIÉN es cliente), gateado por es_partner. */}
        <Route path="/partner" element={<PartnerRoute><PartnerPortal /></PartnerRoute>} />
      </Routes>
    </Suspense>
  );
}
